import { useState, useRef, useEffect } from "react";

const BASE = "https://support.gs-group.fr/api";
const h = () => ({ "Content-Type":"application/json", Authorization:"Bearer "+localStorage.getItem("nexo_token") });

export default function ModalStock({ T, onClose, onCreated }) {
  const [tab, setTab]               = useState("manuel");
  const [clients, setClients]       = useState([]);
  const [scanResult, setScanResult] = useState(null);
  const [searching, setSearching]   = useState(false);
  const [scanInput, setScanInput]   = useState("");
  const [attribClient, setAttribClient] = useState("");
  const [scanMode, setScanMode]         = useState("entree"); // "entree" | "attribuer"
  const [scanQty, setScanQty]           = useState(1);
  const [addedMsg, setAddedMsg]         = useState("");
  const [attribNotes, setAttribNotes]   = useState("");
  const [csvData, setCsvData]       = useState([]);
  const xlsxRef = useRef();
  const [csvPreview, setCsvPreview] = useState([]);
  const [importing, setImporting]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError]   = useState("");
  const [scanFlash, setScanFlash]   = useState(false);
  const videoRef  = useRef(null);
  const readerRef = useRef(null);
  const lastScan  = useRef("");

  const [form, setForm] = useState({
    designation:"", reference:"", categorie:"Caméra",
    quantite:1, seuil_alerte:5, numero_serie:"", notes:""
  });

  const CATS = ["Caméra","NVR/DVR","Switch","Câble","Alimentation","Centrale alarme","Détecteur","Sirène","Clavier","Badge","Accessoire","Autre"];

  useEffect(()=>{
    fetch(`${BASE}/clients`,{headers:h()}).then(r=>r.json()).then(d=>setClients(Array.isArray(d)?d:[]));
    return ()=>stopCamera();
  },[]);

  useEffect(()=>{ if(tab!=="scan") stopCamera(); },[tab]);

  /* ── Caméra ── */
  const startCamera = async () => {
    setCameraError("");
    setScanResult(null);
    setScanInput("");
    lastScan.current = "";
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      readerRef.current = new BrowserMultiFormatReader();
      const devices = await BrowserMultiFormatReader.listVideoInputDevices();
      // Préférer la caméra arrière sur mobile
      const backCam = devices.find(d => /back|rear|environment/i.test(d.label)) || devices[0];
      const deviceId = backCam?.deviceId || undefined;
      setCameraActive(true);
      await readerRef.current.decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
        if (result) {
          const text = result.getText();
          if (text === lastScan.current) return;
          lastScan.current = text;
          setScanInput(text);
          setScanFlash(true);
          setTimeout(()=>setScanFlash(false), 600);
          if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
          handleScan(text);
        }
      });
    } catch(e) {
      setCameraError("Impossible d'accéder à la caméra. Autorisez l'accès dans votre navigateur.");
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    try { readerRef.current?.reset(); } catch(e){}
    readerRef.current = null;
    setCameraActive(false);
  };

  /* ── Recherche produit ── */
  const handleScan = async (val) => {
    if (!val?.trim()) return;
    setSearching(true);
    setScanResult(null);
    setScanQty(1);
    try {
      const res = await fetch(`${BASE}/stock/scan/${encodeURIComponent(val.trim())}`,{headers:h()}).then(r=>r.json());
      if (res?.error) { setError("Produit non trouvé : " + val); setScanResult(null); }
      else { setScanResult(res); setError(""); if(navigator.vibrate) navigator.vibrate(80); }
    } catch(e){ setError("Erreur réseau"); }
    setSearching(false);
  };

  const ajouterStock = async () => {
    if (!scanResult) return;
    setSaving(true);
    const newQty = (scanResult.quantite||0) + scanQty;
    const body = { quantite: newQty };
    if (scanInput?.trim()) body.numero_serie = scanInput.trim();
    await fetch(`${BASE}/stock/${scanResult.id}`,{method:"PATCH",headers:h(),body:JSON.stringify(body)});
    setScanResult(p=>({...p, quantite: newQty}));
    setAddedMsg(`+${scanQty} ajouté(s) · Nouveau stock : ${newQty} ✅`);
    if(navigator.vibrate) navigator.vibrate([100,50,100,50,200]);
    setTimeout(()=>{ setAddedMsg(""); setScanResult(null); setScanInput(""); setScanQty(1); },2500);
    setSaving(false);
  };

  const handleAttribuer = async () => {
    if (!scanResult || !attribClient) return;
    setSaving(true);
    await fetch(`${BASE}/stock/${scanResult.id}/attribuer`,{
      method:"POST", headers:h(),
      body:JSON.stringify({client_id:attribClient, notes:attribNotes, numero_serie:scanInput})
    });
    setSaving(false);
    onCreated();
  };

  const handleManuel = async () => {
    if (!form.designation) { setError("Désignation obligatoire"); return; }
    setSaving(true);
    const res = await fetch(`${BASE}/stock`,{method:"POST",headers:h(),body:JSON.stringify(form)}).then(r=>r.json());
    setSaving(false);
    if (res.error) { setError(res.error); return; }
    onCreated();
  };


  const parseStockRows = (headers, getVal) => {
    const nrm = s => s ? s.normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase().trim() : "";
    const hN = headers.map(nrm);
    const find = (...keys) => { for(const k of keys){const i=hN.findIndex(h=>h.includes(k));if(i>=0)return i;} return -1; };
    const iDes=find("designation","nom","name"); const iRef=find("reference","ref");
    const iCat=find("categorie","category"); const iQty=find("quantite","qty","qte");
    const iSeuil=find("seuil"); const iSN=find("serie","sn");
    const iMrq=find("marque","brand"); const iPU=find("prix public","prix vente");
    const iPN=find("prix net","prix achat");
    const iQ1=hN.findIndex(h=>h.includes("central")||h.includes("paris"));
    const iQ2=hN.findIndex(h=>h.includes("rolling"));
    const iQ3=hN.findIndex(h=>h.includes("logistic"));
    return { iDes,iRef,iCat,iQty,iSeuil,iSN,iMrq,iPU,iPN,iQ1,iQ2,iQ3 };
  };

  const buildRow = (v, idx, iDes,iRef,iCat,iQty,iSeuil,iSN,iMrq,iPU,iPN,iQ1,iQ2,iQ3) => {
    const get = i => i>=0?(v[i]!=null?String(v[i]).replace(/^"|"$/g,"").trim():""):"";
    const designation = (get(iDes)||(get(iMrq)+" "+get(iRef)).trim()).slice(0,255);
    if(!designation) return null;
    let quantite=0;
    if(iQty>=0) quantite=parseInt(get(iQty))||0;
    else if(iQ1>=0||iQ2>=0||iQ3>=0) quantite=(parseInt(get(iQ1))||0)+(parseInt(get(iQ2))||0)+(parseInt(get(iQ3))||0);
    else quantite=1;
    return { designation, reference:(get(iRef)||"REF-"+String(idx+1).padStart(5,"0")).slice(0,100),
      categorie:get(iCat)||"Divers", quantite, seuil_alerte:parseInt(get(iSeuil))||5,
      numero_serie:get(iSN)||"", marque:get(iMrq)||"",
      prix_vente:parseFloat(get(iPU))||null, prix_achat:parseFloat(get(iPN))||null };
  };

  const handleXLSX = (file) => {
    import("xlsx").then(XLSX => {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const wb = XLSX.read(new Uint8Array(e.target.result), {type:"array"});
          const ws = wb.Sheets[wb.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(ws, {header:1, defval:""});
          if(data.length<2) return;
          const headers = data[0].map(h=>String(h||""));
          const {iDes,iRef,iCat,iQty,iSeuil,iSN,iMrq,iPU,iPN,iQ1,iQ2,iQ3} = parseStockRows(headers, null);
          const rows = data.slice(1).map((v,i)=>buildRow(v,i,iDes,iRef,iCat,iQty,iSeuil,iSN,iMrq,iPU,iPN,iQ1,iQ2,iQ3)).filter(Boolean);
          setCsvData(rows); setCsvPreview(rows.slice(0,5));
        } catch(err) { alert("Erreur XLSX: "+err.message); }
      };
      reader.readAsArrayBuffer(file);
    }).catch(()=>alert("SheetJS non disponible"));
  };

  const handleCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result.replace(/\r\n/g,"\n").replace(/\r/g,"\n");
      // Parser CSV robuste gérant les champs entre guillemets
      const parseRow = (line) => {
        const r=[]; let inQ=false, cell="";
        for(let i=0;i<line.length;i++){
          const c=line[i];
          if(c==='"'){inQ=!inQ;}
          else if((c===","||c===";")&&!inQ){r.push(cell.trim());cell="";}
          else{cell+=c;}
        }
        r.push(cell.trim()); return r;
      };
      const lines = text.split("\n").filter(l=>l.trim());
      const rawH = parseRow(lines[0]).map(h=>h.replace(/[\uFEFF"]/g,"").trim());
      const nrm = s=>s.normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase().trim();
      const hN = rawH.map(nrm);
      // Mapping flexible : supporte GS-Group et Tevah
      const find = (...keys) => { for(const k of keys){const i=hN.findIndex(h=>h.includes(k));if(i>=0)return i;} return -1; };
      const iDes  = find("designation","nom","name");
      const iRef  = find("reference","ref");
      const iCat  = find("categorie","category");
      const iQty  = find("quantite","qty","qte");
      const iSeuil= find("seuil");
      const iSN   = find("serie","sn");
      const iMrq  = find("marque","brand");
      const iPU   = find("prix public","public price","prix vente");
      const iPN   = find("prix net","net price","prix achat");
      // Stock Tevah : somme des 3 colonnes de stock
      const iQ1   = hN.findIndex(h=>h.includes("central")||h.includes("paris"));
      const iQ2   = hN.findIndex(h=>h.includes("rolling"));
      const iQ3   = hN.findIndex(h=>h.includes("logistic"));
      const rows = [];
      lines.slice(1).forEach((line,li)=>{
        const v = parseRow(line);
        if(!v.length||v.every(c=>!c.trim())) return;
        const get=(i)=>i>=0?(v[i]||"").replace(/^"|"$/g,"").trim():"";
        const designation = get(iDes)||get(iMrq)+" "+get(iRef);
        if(!designation.trim()) return;
        // Calcul stock Tevah ou colonne unique
        let quantite = 0;
        if(iQty>=0) quantite=parseInt(get(iQty))||0;
        else if(iQ1>=0||iQ2>=0||iQ3>=0){
          quantite=(parseInt(get(iQ1))||0)+(parseInt(get(iQ2))||0)+(parseInt(get(iQ3))||0);
        } else quantite=1;
        const reference = get(iRef)||("REF-"+String(li+1).padStart(5,"0"));
        rows.push({
          designation: designation.slice(0,255),
          reference: reference.slice(0,100),
          categorie: get(iCat)||"Divers",
          quantite,
          seuil_alerte: parseInt(get(iSeuil))||5,
          numero_serie: get(iSN)||"",
          marque: get(iMrq)||"",
          prix_vente: parseFloat(get(iPU))||null,
          prix_achat: parseFloat(get(iPN))||null,
        });
      });
      setCsvData(rows); setCsvPreview(rows.slice(0,5));
    };
    reader.readAsText(file,"UTF-8");
  };

  const handleImportCSV = async () => {
    if (!csvData.length) return;
    setImporting(true);
    const res = await fetch(`${BASE}/stock/import-csv`,{method:"POST",headers:h(),body:JSON.stringify({lignes:csvData})}).then(r=>r.json());
    setImporting(false);
    if (res.error) { setError(res.error); return; }
    onCreated();
  };

  const inp = (key,ph="",type="text") => (
    <input value={form[key]||""} onChange={e=>setForm(p=>({...p,[key]:type==="number"?parseInt(e.target.value)||0:e.target.value}))}
      placeholder={ph} type={type}
      style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 13px",color:T.text,fontSize:14,outline:"none",width:"100%"}}/>
  );

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(8,12,30,0.22)",backdropFilter:"blur(4px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,animation:"nx-modal-open 0.3s cubic-bezier(0.16,1,0.3,1) both",width:580,maxWidth:"95vw",maxHeight:"92vh",display:"flex",flexDirection:"column"}}>

        {/* Header */}
        <div style={{padding:"20px 24px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:T.text}}>📦 Gestion du stock</div>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>Ajout manuel · Import CSV · Scanner code-barres</div>
          </div>
          <button onClick={()=>{stopCamera();onClose();}} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:22}}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{display:"flex",borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
          {[["manuel","✏️ Manuel"],["csv","📄 CSV"],["xlsx","📊 Excel"],["scan","📷 Scanner"]].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)}
              style={{flex:1,padding:"12px",background:tab===k?T.accentDim:"transparent",border:"none",borderBottom:tab===k?`2px solid ${T.accent}`:"2px solid transparent",color:tab===k?T.accent:T.muted,fontSize:13,fontWeight:tab===k?700:500,cursor:"pointer"}}>
              {l}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{flex:1,overflowY:"auto",padding:"20px 24px"}}>
          {error&&<div style={{background:T.dangerDim,border:`1px solid ${T.danger}40`,borderRadius:9,padding:"10px 14px",fontSize:13,color:T.danger,marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>⚠ {error}</span>
            <button onClick={()=>setError("")} style={{background:"none",border:"none",color:T.danger,cursor:"pointer",fontSize:16}}>✕</button>
          </div>}

          {/* ── MANUEL ── */}
          {tab==="manuel"&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {[["designation","Désignation *","Ex: Caméra dôme DAHUA 4MP"],["reference","Référence","REF-001"],["numero_serie","N° de série","SN-XXXXX"]].map(([k,l,ph])=>(
                <div key={k} style={{display:"flex",flexDirection:"column",gap:6}}>
                  <label style={{fontSize:11,fontWeight:700,color:T.mutedL,textTransform:"uppercase",letterSpacing:"0.5px"}}>{l}</label>
                  {inp(k,ph)}
                </div>
              ))}
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <label style={{fontSize:11,fontWeight:700,color:T.mutedL,textTransform:"uppercase",letterSpacing:"0.5px"}}>Catégorie</label>
                <select value={form.categorie} onChange={e=>setForm(p=>({...p,categorie:e.target.value}))}
                  style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 13px",color:T.text,fontSize:14,outline:"none",cursor:"pointer"}}>
                  {CATS.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  <label style={{fontSize:11,fontWeight:700,color:T.mutedL,textTransform:"uppercase",letterSpacing:"0.5px"}}>Quantité</label>
                  {inp("quantite","0","number")}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  <label style={{fontSize:11,fontWeight:700,color:T.mutedL,textTransform:"uppercase",letterSpacing:"0.5px"}}>Seuil d'alerte</label>
                  {inp("seuil_alerte","5","number")}
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <label style={{fontSize:11,fontWeight:700,color:T.mutedL,textTransform:"uppercase",letterSpacing:"0.5px"}}>Notes</label>
                <textarea value={form.notes||""} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={3} placeholder="Notes internes..."
                  style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 13px",color:T.text,fontSize:14,outline:"none",resize:"vertical"}}/>
              </div>
            </div>
          )}

          {/* ── CSV ── */}
          {tab==="csv"&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{background:T.accentDim,border:`1px solid ${T.accent}30`,borderRadius:11,padding:14,fontSize:12,color:T.mutedL,lineHeight:1.7}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:6}}>📄 Format CSV</div>
                <div style={{fontFamily:"monospace",background:T.bg,borderRadius:8,padding:"10px 12px",fontSize:11,color:T.text}}>
                  designation;reference;categorie;quantite;seuil_alerte<br/>
                  Caméra DAHUA 4MP;CAM-001;Caméra;10;3
                </div>
              </div>
              <label style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,padding:28,background:T.bg,borderRadius:13,border:`2px dashed ${csvData.length?T.success:T.border}`,cursor:"pointer"}}>
                <input type="file" accept=".csv,.txt" onChange={e=>e.target.files[0]&&handleCSV(e.target.files[0])} style={{display:"none"}}/>
                <span style={{fontSize:40}}>{csvData.length?"✅":"📄"}</span>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:14,fontWeight:700,color:T.text}}>{csvData.length?`${csvData.length} produits détectés`:"Charger un fichier CSV"}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:3}}>Cliquer pour sélectionner</div>
                </div>
              </label>
              {csvPreview.length>0&&(
                <div style={{background:T.bg,borderRadius:11,border:`1px solid ${T.border}`,overflow:"auto"}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,padding:"10px 14px",borderBottom:`1px solid ${T.border}`}}>Aperçu — {csvData.length} lignes</div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead><tr style={{background:T.card}}>{["Désignation","Ref","Cat","Qté"].map(h2=><th key={h2} style={{padding:"7px 10px",textAlign:"left",color:T.muted,fontWeight:700}}>{h2}</th>)}</tr></thead>
                    <tbody>{csvPreview.map((r,i)=><tr key={i}><td style={{padding:"7px 10px",color:T.text,fontWeight:600}}>{r.designation}</td><td style={{padding:"7px 10px",color:T.muted,fontFamily:"monospace"}}>{r.reference||"—"}</td><td style={{padding:"7px 10px",color:T.accent}}>{r.categorie}</td><td style={{padding:"7px 10px",fontWeight:700}}>{r.quantite}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── EXCEL ── */}
          {tab==="xlsx"&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{background:T.warningDim,border:`1px solid ${T.warning}30`,borderRadius:11,padding:14,fontSize:12,color:T.mutedL,lineHeight:1.7}}>
                <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:4}}>📊 Import Excel</div>
                <div>Compatible catalogue Tevah · La 1ère feuille sera importée automatiquement · Colonnes détectées auto</div>
              </div>
              <input ref={xlsxRef} type="file" accept=".xlsx,.xls" style={{display:"none"}} onChange={e=>e.target.files[0]&&handleXLSX(e.target.files[0])}/>
              <div onClick={()=>xlsxRef.current?.click()} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,padding:28,background:T.bg,borderRadius:13,border:`2px dashed ${csvData.length?T.success:T.border}`,cursor:"pointer"}}>
                <span style={{fontSize:40}}>{csvData.length?"✅":"📊"}</span>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:14,fontWeight:700,color:T.text}}>{csvData.length?`${csvData.length} produits détectés`:"Charger un fichier Excel"}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:3}}>.xlsx ou .xls · Cliquer pour sélectionner</div>
                </div>
              </div>
              {csvPreview.length>0&&(
                <div style={{background:T.bg,borderRadius:11,border:`1px solid ${T.border}`,overflow:"auto"}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,padding:"10px 14px",borderBottom:`1px solid ${T.border}`}}>Aperçu — {csvData.length} produits</div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead><tr style={{background:T.card}}>{["Désignation","Ref","Marque","Qté","Prix achat"].map(h2=><th key={h2} style={{padding:"7px 10px",textAlign:"left",color:T.muted,fontWeight:700}}>{h2}</th>)}</tr></thead>
                    <tbody>{csvPreview.map((r,i)=><tr key={i} style={{borderBottom:`1px solid ${T.border}15`}}><td style={{padding:"7px 10px",color:T.text,fontWeight:600,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.designation}</td><td style={{padding:"7px 10px",color:T.muted,fontFamily:"monospace",fontSize:10}}>{r.reference||"—"}</td><td style={{padding:"7px 10px",color:T.mutedL}}>{r.marque||"—"}</td><td style={{padding:"7px 10px",fontWeight:700}}>{r.quantite}</td><td style={{padding:"7px 10px",color:T.accent}}>{r.prix_achat?r.prix_achat+"€":"—"}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── SCAN CAMÉRA ── */}
          {tab==="scan"&&(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>

              {/* Viewfinder caméra */}
              <div style={{position:"relative",background:"#000",borderRadius:16,overflow:"hidden",aspectRatio:"4/3",maxHeight:280}}>
                <video ref={videoRef} style={{width:"100%",height:"100%",objectFit:"cover",display:cameraActive?"block":"none"}} playsInline muted/>

                {/* Overlay scanning */}
                {cameraActive&&(
                  <>
                    {/* Coins du viseur */}
                    {[["top:16px","left:16px","borderTop","borderLeft"],["top:16px","right:16px","borderTop","borderRight"],["bottom:16px","left:16px","borderBottom","borderLeft"],["bottom:16px","right:16px","borderBottom","borderRight"]].map(([t,lr,b1,b2],i)=>(
                      <div key={i} style={{position:"absolute",...Object.fromEntries([[t.split(":")[0],t.split(":")[1]],[lr.split(":")[0],lr.split(":")[1]]]),width:28,height:28,[b1]:`3px solid ${scanFlash?"#22c55e":T.accent}`,[b2]:`3px solid ${scanFlash?"#22c55e":T.accent}`,borderRadius:3,transition:"border-color 0.2s"}}/>
                    ))}
                    {/* Ligne de scan animée */}
                    <div style={{position:"absolute",left:"10%",right:"10%",height:2,background:`linear-gradient(90deg,transparent,${T.accent},transparent)`,animation:"scanLine 2s ease-in-out infinite",top:"50%"}}/>
                    <style>{`@keyframes scanLine{0%{top:20%}50%{top:80%}100%{top:20%}}`}</style>
                    {/* Flash succès */}
                    {scanFlash&&<div style={{position:"absolute",inset:0,background:"rgba(34,197,94,0.3)",borderRadius:16,transition:"opacity 0.3s"}}/>}
                  </>
                )}

                {/* Placeholder avant activation */}
                {!cameraActive&&!cameraError&&(
                  <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:20}}>
                    <div style={{fontSize:56}}>📷</div>
                    <div style={{fontSize:14,fontWeight:700,color:"#fff",textAlign:"center"}}>Scanner un code-barres ou QR code</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",textAlign:"center"}}>Pointez la caméra vers le code à scanner</div>
                    <button onClick={startCamera}
                      style={{padding:"12px 28px",borderRadius:12,background:T.accent,border:"none",color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer",marginTop:6,boxShadow:"0 4px 20px rgba(0,0,0,0.4)"}}>
                      🎥 Démarrer la caméra
                    </button>
                  </div>
                )}

                {/* Erreur caméra */}
                {cameraError&&(
                  <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,padding:20}}>
                    <div style={{fontSize:40}}>⚠️</div>
                    <div style={{fontSize:13,color:"#f87171",textAlign:"center",fontWeight:600}}>{cameraError}</div>
                    <button onClick={startCamera} style={{padding:"10px 20px",borderRadius:10,background:T.accent,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer"}}>Réessayer</button>
                  </div>
                )}

                {/* Bouton stop */}
                {cameraActive&&(
                  <button onClick={stopCamera}
                    style={{position:"absolute",top:10,right:10,background:"rgba(0,0,0,0.6)",border:"none",color:"#fff",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer",backdropFilter:"blur(4px)"}}>
                    ⏹ Arrêter
                  </button>
                )}

                {/* Indicator recherche */}
                {searching&&(
                  <div style={{position:"absolute",bottom:12,left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.75)",color:"#fff",borderRadius:20,padding:"6px 16px",fontSize:12,fontWeight:600,backdropFilter:"blur(4px)"}}>
                    ⏳ Recherche en cours...
                  </div>
                )}
              </div>

              {/* Input manuel fallback */}
              <div style={{display:"flex",gap:8}}>
                <input value={scanInput} onChange={e=>setScanInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleScan(scanInput)}
                  placeholder="Ou entrer manuellement le N° série / code-barres..."
                  style={{flex:1,background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 13px",color:T.text,fontSize:13,outline:"none",fontFamily:"monospace"}}/>
                <button onClick={()=>handleScan(scanInput)} disabled={searching||!scanInput}
                  style={{padding:"10px 16px",borderRadius:10,background:scanInput?T.accent:T.border,border:"none",color:"#fff",fontWeight:700,cursor:scanInput?"pointer":"not-allowed",flexShrink:0}}>
                  {searching?"⏳":"🔍"}
                </button>
              </div>

              {/* Résultat */}
              {/* Toggle mode */}
              <div style={{display:"flex",background:T.bg,borderRadius:10,border:`1px solid ${T.border}`,overflow:"hidden"}}>
                {[["entree","📦 Entrée stock"],["attribuer","👤 Attribuer client"]].map(([m,l])=>(
                  <button key={m} onClick={()=>setScanMode(m)}
                    style={{flex:1,padding:"10px",background:scanMode===m?T.accentDim:"transparent",border:"none",color:scanMode===m?T.accent:T.muted,fontSize:13,fontWeight:scanMode===m?700:500,cursor:"pointer"}}>
                    {l}
                  </button>
                ))}
              </div>

              {scanResult&&(
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  {/* Produit trouvé */}
                  <div style={{background:`${T.success}15`,border:`2px solid ${T.success}40`,borderRadius:13,padding:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:T.success,marginBottom:10,textTransform:"uppercase",letterSpacing:"0.5px"}}>✅ Produit trouvé</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      {[["📦 Désignation",scanResult.designation||scanResult.nom],["🏷️ Référence",scanResult.reference||"—"],["📂 Catégorie",scanResult.categorie||"—"],["🔢 Stock actuel",`${scanResult.quantite} unité(s)`]].map(([label,val])=>(
                        <div key={label} style={{background:T.card,borderRadius:8,padding:"9px 12px",border:`1px solid ${T.border}`}}>
                          <div style={{fontSize:10,color:T.muted,marginBottom:2}}>{label}</div>
                          <div style={{fontSize:13,fontWeight:600,color:T.text}}>{val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mode Entrée stock */}
                  {scanMode==="entree"&&(
                    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16,display:"flex",flexDirection:"column",gap:14}}>
                      <div style={{fontSize:13,fontWeight:700,color:T.text}}>📦 Entrée stock</div>

                      {/* N° de série */}
                      <div>
                        <label style={{fontSize:10,fontWeight:700,color:T.mutedL,textTransform:"uppercase",letterSpacing:"0.5px",display:"block",marginBottom:5}}>N° de série / Code scanné</label>
                        <input value={scanInput||""} onChange={e=>setScanInput(e.target.value)}
                          placeholder="Scanné automatiquement..."
                          style={{width:"100%",background:T.bg,border:`1px solid ${T.accent}60`,borderRadius:9,padding:"10px 12px",color:T.accent,fontSize:13,fontFamily:"monospace",fontWeight:600,outline:"none"}}/>
                      </div>

                      {/* Quantité */}
                      <div>
                        <label style={{fontSize:10,fontWeight:700,color:T.mutedL,textTransform:"uppercase",letterSpacing:"0.5px",display:"block",marginBottom:8}}>Quantité à ajouter</label>
                        <div style={{display:"flex",alignItems:"center",gap:12}}>
                          <button onClick={()=>setScanQty(q=>Math.max(1,q-1))}
                            style={{width:48,height:48,borderRadius:10,border:`1px solid ${T.border}`,background:T.bg,color:T.text,fontSize:26,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>−</button>
                          <input type="number" value={scanQty} min={1}
                            onChange={e=>setScanQty(Math.max(1,parseInt(e.target.value)||1))}
                            style={{flex:1,textAlign:"center",background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px",color:T.text,fontSize:28,fontWeight:800,outline:"none"}}/>
                          <button onClick={()=>setScanQty(q=>q+1)}
                            style={{width:48,height:48,borderRadius:10,border:`1px solid ${T.border}`,background:T.bg,color:T.text,fontSize:26,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>+</button>
                        </div>
                      </div>

                      {/* Résumé */}
                      <div style={{background:T.bg,borderRadius:9,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:12,color:T.muted}}>Stock après ajout</span>
                        <span style={{fontSize:20,fontWeight:800,color:T.accent}}>{(scanResult.quantite||0)+scanQty} <span style={{fontSize:12,color:T.muted}}>unité(s)</span></span>
                      </div>

                      {addedMsg&&<div style={{textAlign:"center",fontSize:14,fontWeight:700,color:T.success,padding:"10px",background:`${T.success}15`,borderRadius:9}}>{addedMsg}</div>}

                      <button onClick={ajouterStock} disabled={saving}
                        style={{width:"100%",padding:14,borderRadius:11,background:T.accent,border:"none",color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer",opacity:saving?0.7:1}}>
                        {saving?"Enregistrement...":"✅ Confirmer l'entrée"}
                      </button>
                    </div>
                  )}

                  {/* Mode Attribuer client */}
                  {scanMode==="attribuer"&&(
                    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:16}}>
                      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>👤 Attribuer à un client</div>
                      <div style={{display:"flex",flexDirection:"column",gap:10}}>
                        <select value={attribClient} onChange={e=>setAttribClient(e.target.value)}
                          style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none",cursor:"pointer"}}>
                          <option value="">— Sélectionner un client —</option>
                          {clients.map(c=><option key={c.id} value={c.id}>{c.raison_sociale||c.nom}</option>)}
                        </select>
                        <textarea value={attribNotes} onChange={e=>setAttribNotes(e.target.value)} rows={2} placeholder="Notes..."
                          style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 13px",color:T.text,fontSize:13,outline:"none",resize:"vertical"}}/>
                        <button onClick={handleAttribuer} disabled={!attribClient||saving}
                          style={{padding:11,borderRadius:10,background:attribClient?T.accent:T.border,border:"none",color:"#fff",fontSize:14,fontWeight:700,cursor:attribClient?"pointer":"not-allowed"}}>
                          {saving?"Attribution...":"✅ Attribuer ce produit"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{fontSize:11,color:T.muted,textAlign:"center",lineHeight:1.6}}>
                📱 Sur mobile : pointez la caméra vers le code-barres<br/>
                🖥️ Sur PC : utilisez un lecteur USB ou saisie manuelle
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{padding:"14px 24px",borderTop:`1px solid ${T.border}`,display:"flex",gap:10,flexShrink:0}}>
          <button onClick={()=>{stopCamera();onClose();}} style={{flex:1,padding:11,borderRadius:10,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,fontSize:14,fontWeight:600,cursor:"pointer"}}>Annuler</button>
          {tab==="manuel"&&(
            <button onClick={handleManuel} disabled={saving||!form.designation}
              style={{flex:2,padding:11,borderRadius:10,background:form.designation?T.accent:T.border,border:"none",color:"#fff",fontSize:14,fontWeight:700,cursor:form.designation?"pointer":"not-allowed"}}>
              {saving?"Enregistrement...":"✅ Ajouter au stock"}
            </button>
          )}
          {(tab==="csv"||tab==="xlsx")&&(
            <button onClick={handleImportCSV} disabled={importing||!csvData.length}
              style={{flex:2,padding:11,borderRadius:10,background:csvData.length?T.accent:T.border,border:"none",color:"#fff",fontSize:14,fontWeight:700,cursor:csvData.length?"pointer":"not-allowed"}}>
              {importing?"Import...":csvData.length?`📤 Importer ${csvData.length} produit(s)`:"📤 Importer"}
            </button>
          )}
          {tab==="scan"&&!cameraActive&&(
            <button onClick={startCamera}
              style={{flex:2,padding:11,borderRadius:10,background:T.accent,border:"none",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer"}}>
              🎥 Démarrer la caméra
            </button>
          )}
          {tab==="scan"&&cameraActive&&(
            <button onClick={stopCamera}
              style={{flex:2,padding:11,borderRadius:10,background:T.dangerDim,border:`1px solid ${T.danger}40`,color:T.danger,fontSize:14,fontWeight:700,cursor:"pointer"}}>
              ⏹ Arrêter la caméra
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
